/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { IFontData, ILayoutData, IParsedUnit, IWordWrapOptions } from './fontpath.interfaces';
import * as Wrapper from 'opentype-layout-improved';
/**
 * An enum used to control the wrapping mode.
 *
 * 'PRE' = maintain spacing
 * "NO_WRAP" = Collapse whitespace but only break on newline characters.
 */
export enum WORD_WRAP_MODE {
	'PRE' = 'pre',
	'NO_WRAP' = 'nowrap',
	'GREEDY' = 'greedy',
}
/**
 * The options used to control the WordWrap:
 *
 * width = The width of the box in font units
 * align = The alignment of the text within its width
 * letterSpacing = The additional letter spacing in font units
 * lineHeight = The line height in font units. Default = 1.175 * font.unitsPerEm
 * start = The starting character index into text to layout.
 * end = The ending index into text to layout. This is exclusive.
 * mde = Value from WORD_WRAP_MODE
 */

/**
 * WordWrapper utility class. All methods within are static.
 */
export class WordWrapper {
	/**
	 * The wrap method is responsible for computing the drawing positions for each glyph in the string.
	 *
	 * For the wrapping options, it assumes all units are in Font Units ('em')
	 */
	public static wrap(font: IFontData, text: string, options?: IWordWrapOptions): ILayoutData {
		return Wrapper(font, text, options);
	}
	/**
	 * Returns the font units for the given inputs
	 */
	public static getEmUnits(font: IFontData, fontSize: number, value: number | string): number {
		const parsed: IParsedUnit = WordWrapper.parseUnit(value);
		if (typeof value === 'number') {
			parsed[1] = 'px';
		}
		if (parsed[1] === 'em') {
			return parsed[0] * font.unitsPerEm;
		} else if (parsed[1] === 'px') {
			const pxScale = (1 / font.unitsPerEm) * fontSize;
			return parsed[0] / pxScale;
		} else {
			throw new Error('Invalid unit for getPixelSize: ' + parsed[1]);
		}
	}

	public static getPxUnits(font: IFontData, fontSize: number, value: number): number {
		const pxScale = font.unitsPerEm / fontSize;
		return value / pxScale;
	}
	/**
	 * Returns the font size
	 */
	public static getFontSize(font: IFontData, value: string | number, dpi: number = 1): number {
		const parsed: IParsedUnit = WordWrapper.parseUnit(value);
		return parsed[0] * dpi;
	}
	/**
	 * Get the current scale
	 */
	public static getScale(font: IFontData, fontSizePx: number): number {
		return (1 / font.unitsPerEm) * fontSizePx;
	}

	public static getPx(value: string | number): number {
		const parsedValue: IParsedUnit = WordWrapper.parseUnit(value);
		return parsedValue[0];
	}
	/**
	 * Returns an array where idx 0 is the value and idx 1 is the unit. The unit will be '' if the passed in value does
	 * not have a unit. Example: '10em' will return [10, 'em'] and '10' will return [10, '']
	 */
	public static parseUnit(str: string | number, out: IParsedUnit = [0, '']): IParsedUnit {
		if (!out) {
			out = [0, ''];
		}

		str = String(str);
		out[0] = parseFloat(str);
		out[1] = str.match(/[\d.\-\+]*\s*(.*)/)[1] || '';
		return out;
	}
}
